import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { REGION_LABELS_BY_ID } from '@/app/services/serviceScheduler';
import {
  Button,
  ColorPicker,
  Dropdown,
  InputText,
} from '@/components/common/service-scheduler/compat-controls';

interface Props {
  user: {
    name: string;
    calendarName?: string;
    region?: string;
    site?: string;
    color?: string;
  };
  onSave?: (updated: Props['user']) => void | Promise<void>;
}

export default function UserSettingsForm({ user, onSave }: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState(user);
  const [isSaving, setIsSaving] = useState(false);

  const handle =
    (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement> | { value: string }) =>
      setForm({ ...form, [field]: 'target' in e ? e.target.value : e.value });

  const handleSave = async () => {
    if (!onSave || isSaving) return;
    setIsSaving(true);
    try {
      await onSave(form);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <InputText
        placeholder={t('common.name')}
        value={form.name}
        onChange={handle('name')}
        className="w-full"
      />

      <InputText
        placeholder={t('serviceScheduler.calendarName')}
        value={form.calendarName ?? ''}
        onChange={handle('calendarName')}
        className="w-full"
      />

      <div className="grid grid-cols-2 gap-2">
        <Dropdown
          placeholder={t('serviceScheduler.region')}
          value={form.region ?? ''}
          options={Object.values(REGION_LABELS_BY_ID)}
          onChange={handle('region')}
          className="w-full"
        />
        <InputText
          placeholder={t('site.site')}
          value={form.site ?? ''}
          onChange={handle('site')}
          className="w-full"
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium">
          {t('common.color')}
        </label>
        <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5">
          <ColorPicker
            value={form.color ?? '#0096ff'}
            onChange={handle('color')}
            inline
          />
          <InputText
            value={(form.color ?? '#0096ff').toUpperCase()}
            onChange={handle('color')}
            className="h-8 border-0 bg-transparent px-0 text-xs font-medium tracking-wide text-slate-600 focus-visible:ring-0"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button
          label={isSaving ? t('common.saving') : t('common.save')}
          icon="pi pi-check"
          onClick={handleSave}
          loading={isSaving}
          size="small"
        />
      </div>
    </div>
  );
}
